import React from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { text, colors } from '../../core/styles/theme/default';
import Spacer from '../../helpers/Spacer';
import { IconSVG } from '../../primitives';
import ImageWithCaption from '../../components/Image/ImageWithCaption';
import { ImageInterface } from '~/types';

const StyledPublication = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledPublicationImage = styled.div`
  display: block;
  width: 100%;
  padding-bottom: 50%;
  position: relative;
  overflow: hidden;
`;

const StyledBackground = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  background: linear-gradient(
    90deg,
    rgba(37, 55, 100, 0.9) 0%,
    rgba(37, 55, 100, 0.6) 100%
  );

  picture,
  figure,
  .gatsby-image-wrapper {
    width: 100%;
    height: 100%;
  }

  figure {
    opacity: 0.4;
    backface-visibility: hidden;
  }

  picture {
    position: absolute;
    left: 0;
    top: 0;
  }
`;

const StyledPublicationInfo = styled.div`
  display: inline-flex;
  flex-direction: column;
  padding: ${rem(12)} ${rem(34)} ${rem(18)} ${rem(12)};

  ${({ theme }) => theme?.stylesExtension?.publication?.info}
`;

const StyledPublicationFooter = styled.div`
  display: flex;
  height: ${rem(48)};
  background-color: #f7fafc;
  border-radius: 0 0 ${rem(4)} ${rem(4)};
  border-top: ${rem(1)} solid ${colors.grue300};
  box-sizing: content-box;
`;

const StyledPublicationDownloads = styled.div`
  display: flex;
  flex: 1;
`;

const StyledPublicationScroll = styled.div`
  overflow-x: auto;
  overflow-y: none;
  flex: 1;
`;

const StyledPublicationBasket = styled.a`
  display: flex;
  width: ${rem(40)};
  justify-content: center;
  align-items: center;
  border-left: ${rem(1)} solid ${colors.grue300};
  box-sizing: content-box;
  font-size: ${rem(12)};
  font-weight: 500;
  cursor: pointer;
  padding: 0 ${rem(4)};

  ${({ theme }) => theme?.stylesExtension?.publication?.basket}
`;

const StyledPublicationContent = styled.div`
  border: ${rem(1)} solid ${colors.grue300};
  flex: 1;
  justify-content: space-between;
  display: flex;
  flex-direction: column;
  border-radius: 0 0 ${rem(4)} ${rem(4)};
  background-color: ${colors.white};
`;

const StyledPublicationLink = styled.a`
  width: ${rem(44)};
  height: ${rem(48)};
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  font-size: ${rem(12)};
  padding: 0 ${rem(4)};

  &:hover {
    background-color: ${colors.blue300};

    &:after {
      display: none;
    }
  }

  &:after {
    content: '';
    width: ${rem(1)};
    height: ${rem(24)};
    top: 50%;
    background: ${colors.grue300};
    position: absolute;
    left: ${rem(-1)};
    transform: translateY(-50%);
  }

  &:focus {
    &::after {
      box-sizing: border-box !important;
    }
  }

  .m-richtext & {
    text-decoration: none;
  }

  ${({ theme }) => theme?.stylesExtension?.publication?.link}
`;

const StyledPublicationFormat = styled.span`
  ${text.p4}
  background-color: ${colors.white};
  padding: ${rem(4)};
  border-radius: ${rem(3)};
  border-radius: ${rem(2)};
  text-transform: uppercase;
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};

  ${({ theme }) => theme?.stylesExtension?.publication?.format}
`;

const StyledPublicationTitle = styled.p`
  ${text.p4}
  color: ${colors.blue900};
  font-weight: 500;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;

  ${({ theme }) => theme?.stylesExtension?.publication?.title}
`;

const StyledPublicationDate = styled.span`
  font-size: ${rem(12)};
  color: ${colors.grey700};
  font-weight: 100;

  ${({ theme }) => theme?.stylesExtension?.publication?.date}
`;

const Publication = ({ data }: PublicationProps) => {
  const {
    publicationDate,
    publicationId,
    format,
    title,
    shopLink,
    teaserImage,
    localeData,
    basketText,
    trackClick,
  } = data;

  const mtmGetPubTitle = (title: string) => {
    window._mtm.push({ event: 'downloadFile', downloadName: title });
    return;
  };

  return (
    <>
      <StyledPublication data-id={publicationId} className={'m-publication'}>
        <StyledPublicationImage>
          <StyledBackground>
            {teaserImage ? (
              <ImageWithCaption
                data={{
                  image: teaserImage,
                  focusPoint: 'top',
                  disableLink: true,
                }}
              />
            ) : null}
          </StyledBackground>
          <StyledPublicationFormat>{format}</StyledPublicationFormat>
        </StyledPublicationImage>
        <StyledPublicationContent>
          <StyledPublicationInfo>
            <StyledPublicationTitle>{title}</StyledPublicationTitle>
            <Spacer space="basic" />
            <StyledPublicationDate>{publicationDate}</StyledPublicationDate>
          </StyledPublicationInfo>
          <StyledPublicationFooter>
            <StyledPublicationScroll>
              {localeData && (
                <StyledPublicationDownloads>
                  {localeData.map(
                    ({
                      title,
                      fileUrl,
                      lang,
                    }: PublicationProps['data']['localeData'][0]) =>
                      fileUrl && (
                        <StyledPublicationLink
                          key={lang}
                          href={fileUrl}
                          title={title}
                          onClick={() => {
                            trackClick?.();
                            mtmGetPubTitle(title);
                          }}
                        >
                          {lang.toUpperCase()}
                        </StyledPublicationLink>
                      ),
                  )}
                </StyledPublicationDownloads>
              )}
            </StyledPublicationScroll>
            {shopLink && (
              <StyledPublicationBasket
                href={shopLink}
                target="_blank"
                aria-label={basketText ?? 'Basket'}
                onClick={() => {
                  trackClick?.();
                }}
              >
                <IconSVG iconName="EchIconShoppingCart" iconSize="16" />
              </StyledPublicationBasket>
            )}
          </StyledPublicationFooter>
        </StyledPublicationContent>
      </StyledPublication>
    </>
  );
};

export type PublicationProps = {
  data: {
    publicationDate: string;
    publicationId: string;
    format: string;
    title: string;
    shopLink: string | null;
    teaserImage?: ImageInterface['image'];
    localeData: LocaleData[];
    basketText?: string;
    trackClick?: () => void;
  };
};

export type LocaleData = {
  title: string;
  fileUrl: string;
  lang: string;
};

export default Publication;
